import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { IUser } from '../../types/interfaces/IUser'
import { AUTH_USER_TOKEN_KEY } from 'constants/auth'
import { IPersonalDetails } from 'types/interfaces/IPersonalDetails'
import { IAddress } from 'types/interfaces/IAddress'
import { IdentityProvider } from 'types/types/IdentityProvider'
import {
  clearAuthCookies,
  saveAuthDetailsInCookies,
} from 'shared/helpers/loginUtils'
import { UserAuth } from 'types/types/AuthProps'

const initialState: IUser = {
  isAuthenticated: false,
  isCognitoSignedIn: false,
  isFanzaErrorExists: false,
  password: '',

  email: '',
  terms: false,
  newsletter: true,
  toptixId: '',
  roboticketId: '',
  personalDetails: {
    identityProvider: IdentityProvider.ID,
    id: '',
    dob: undefined,
    phoneNum: '',
    firstName: '',
    lastName: '',
    gender: undefined,
    addresses: [
      {
        country: '',
        city: '',
        street: '',
        buildingNumber: '',
        postalCode: '',
      },
    ],
  },
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    userLogin(
      state,
      action: PayloadAction<{ password: string; email: string }>,
    ) {
      const { password, email } = action.payload
      state.isAuthenticated = false
      state.password = password
      state.email = email
    },
    userAuth(state, action: PayloadAction<UserAuth>) {
      const {
        isAuthenticated,
        email,
        toptixId,
        newsletter,
        seenMarketingMessage,
      } = action.payload
      state.isAuthenticated = isAuthenticated

      if (email) {
        state.email = email
      }
      if (toptixId) {
        state.toptixId = toptixId
      }
      state.newsletter = newsletter
      state.personalDetails = { ...state.personalDetails, seenMarketingMessage }
    },
    resetUser() {
      return initialState
    },
    updateUser(state, action: PayloadAction<Partial<IUser>>) {
      return {
        ...state,
        ...action.payload,
        personalDetails: {
          ...state.personalDetails,
          ...action.payload.personalDetails,
          addresses: [
            {
              ...state.personalDetails?.addresses?.[0],
              ...action.payload.personalDetails?.addresses?.[0],
            },
          ],
        },
      }
    },
    updateUserPersonalDetails(
      state,
      action: PayloadAction<Partial<IPersonalDetails>>,
    ) {
      state.personalDetails = {
        ...state.personalDetails,
        ...action.payload,
      }
    },
    updateUserAddress(state, action: PayloadAction<Partial<IAddress>>) {
      if (!state.personalDetails) {
        state.personalDetails = {
          addresses: [action.payload],
        }
      } else {
        state.personalDetails.addresses = [
          {
            ...state.personalDetails.addresses?.[0],
            ...action.payload,
          },
        ]
      }
    },
    setToken(_, action: PayloadAction<string | undefined>) {
      const isUseCookieAuth = process.env.REACT_APP_USE_COOKIE_AUTH === 'true'
      if (action.payload) {
        localStorage.setItem(AUTH_USER_TOKEN_KEY, action.payload)
        if (isUseCookieAuth) {
          saveAuthDetailsInCookies(action.payload)
        }
      } else {
        localStorage.removeItem(AUTH_USER_TOKEN_KEY)
        if (isUseCookieAuth) {
          clearAuthCookies()
        }
      }
    },

    setIsAuthenticated(state, action: PayloadAction<boolean>) {
      state.isAuthenticated = action.payload
    },
    setIsCognitoSignedIn(state, action: PayloadAction<boolean>) {
      state.isCognitoSignedIn = action.payload
    },
    setIsFanzaErrorExists(state, action: PayloadAction<boolean>) {
      state.isFanzaErrorExists = action.payload
    },
  },
})

// Export actions and reducer.
export const {
  userAuth,
  userLogin,
  resetUser,
  updateUser,
  updateUserPersonalDetails,
  updateUserAddress,
  setToken,
  setIsAuthenticated,
  setIsCognitoSignedIn,
  setIsFanzaErrorExists,
} = userSlice.actions
export default userSlice.reducer
